import {
  Button,
  LoadingOverlay,
  Select,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { findKey, toNumber } from 'lodash/fp';
import React, { FC, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Card } from 'reactstrap';
import styled from 'styled-components';

import {
  IncidentType,
  useDevice,
  useIncidentById,
  useUpdateIncident,
  useWithSupportCenter,
} from '@portals/api/organizations';
import { ModalButton } from '@portals/framework';
import {
  EntityAccess,
  useHasEntityAccess,
} from '@portals/framework/EntityAccess';
import { RouteModalLink } from '@portals/framework/route-modals';
import { ReactComponent as ArrowUp } from '@portals/icons/linear/arrow-up.svg';
import { ReactComponent as CloseSquare } from '@portals/icons/linear/close-square.svg';
import { BadgeCell, InfoTable } from '@portals/table';
import { AccessLevelEnum, IncidentPriorityLevel } from '@portals/types';
import {
  getIncidentPriorityLevelName,
  prettyTime,
  PRIORITY_OPTIONS,
  timeAgo,
} from '@portals/utils';

interface IncidentDetailsProps {
  incident: IncidentType;
}

export const IncidentDetails: FC<IncidentDetailsProps> = ({ incident }) => {
  const updateIncident = useUpdateIncident();
  const navigate = useNavigate();
  const device = useDevice(incident.device_id);

  const [title, setTitle] = useState(incident.title);
  const [description, setDescription] = useState(incident.description);
  const [priority, setPriority] = useState(PRIORITY_OPTIONS[incident.priority]);

  const withSupportCenter = useWithSupportCenter();
  const withClose = incident?.status !== 'closed';
  const hasEditAccess = useHasEntityAccess(device.data, AccessLevelEnum.Edit);

  const isIncidentDataChanged = useMemo(
    () =>
      title !== incident.title ||
      description !== incident.description ||
      priority !== PRIORITY_OPTIONS[incident.priority],
    [incident, title, description, priority]
  );

  const onSaveIncidentChanges = async () => {
    const priorityNumber = toNumber(
      findKey((value) => value === priority, PRIORITY_OPTIONS)
    ) as IncidentPriorityLevel;

    await updateIncident.mutateAsync({
      deviceId: incident.device_id,
      incident: {
        id: incident.id,
        title,
        description,
        priority: priorityNumber,
      },
    });

    navigate('/incidents');
  };

  if (!device.isFetched) return <LoadingOverlay visible />;
  if (!device.data) return <Navigate to="/incidents" replace />;

  return (
    <StyledCard color="white">
      <InfoTable.Table>
        <InfoTable.Header>
          {withSupportCenter ? (
            <EntityAccess
              id="escalate-incident"
              minLevel={AccessLevelEnum.Edit}
              entity={device.data}
            >
              <ModalButton
                className="mr-2"
                modalName="CreateTicket"
                label="Escalate"
                data-testid="incident-information-escalate-button"
                color="red"
                leftIcon={<ArrowUp />}
                data={{
                  device: device.data,
                  description: incident.description,
                }}
              />
            </EntityAccess>
          ) : null}

          {withClose ? (
            <EntityAccess
              id="close-incident"
              minLevel={AccessLevelEnum.Edit}
              entity={device.data}
            >
              <ModalButton
                modalName="CloseIncident"
                label="Close"
                data={{ incident, onSuccess: () => navigate('/incidents') }}
                data-testid="close-incident-btn"
                leftIcon={<CloseSquare />}
              />
            </EntityAccess>
          ) : null}
        </InfoTable.Header>
        <InfoTable.Row isHeader label="General" value={null} />
        <InfoTable.Row label="ID" value={incident.id} />
        <InfoTable.Row
          label="Title"
          value={
            <>
              {hasEditAccess ? (
                <TextInput
                  value={title}
                  data-testid="incident-information-title-input"
                  onChange={(event) => setTitle(event.target.value)}
                />
              ) : (
                incident.title
              )}
            </>
          }
        />
        <InfoTable.Row
          label="Description"
          value={
            <>
              {hasEditAccess ? (
                <Textarea
                  minRows={6}
                  maxRows={6}
                  value={description}
                  data-testid="incident-information-description-input"
                  onChange={(event) => setDescription(event.target.value)}
                />
              ) : (
                <pre>
                  <Text size="sm" color="gray.5">
                    {incident.description}
                  </Text>
                </pre>
              )}
            </>
          }
        />
        <InfoTable.Row
          label="Space"
          value={
            <Link to={`/overview/${incident.space_id}`}>
              {incident.space_name}
            </Link>
          }
        />
        <InfoTable.Row
          label="Device"
          value={
            <RouteModalLink
              modalId="device"
              pathParams={[incident.device_id, 'incidents']}
            >
              {device.data.name}
            </RouteModalLink>
          }
        />

        <InfoTable.Row isHeader label="Details" value={null} />

        <InfoTable.Row
          label="Priority"
          value={
            <>
              {hasEditAccess ? (
                <Select
                  data={Object.values(PRIORITY_OPTIONS)}
                  value={priority}
                  onChange={(
                    value: (typeof PRIORITY_OPTIONS)[IncidentPriorityLevel]
                  ) => setPriority(value)}
                  sx={{ maxWidth: 150 }}
                />
              ) : (
                getIncidentPriorityLevelName(incident.priority)
              )}
            </>
          }
        />
        <InfoTable.Row
          label="Status"
          value={<BadgeCell label={incident.status} color="blue" />}
        />
        <InfoTable.Row label="Issue" value={incident.issue} />

        <InfoTable.Row isHeader label="History" value={null} />

        <InfoTable.Row
          label="Created"
          value={prettyTime(incident.created_at)}
        />

        {incident.updated_at ? (
          <InfoTable.Row
            label="Last Updated"
            value={prettyTime(incident.updated_at)}
          />
        ) : null}
        {!withClose && incident.closed_at ? (
          <>
            <InfoTable.Row
              label="Closed"
              value={timeAgo(Date.parse(incident.closed_at))}
            />
            <InfoTable.Row label="TTC" value={incident.ttc} />
          </>
        ) : null}
      </InfoTable.Table>
      <Button
        disabled={!isIncidentDataChanged}
        onClick={onSaveIncidentChanges}
        data-testid="incidents-information-save-changes-button"
        sx={(theme) => ({
          width: 150,
          marginTop: theme.spacing.lg,
          alignSelf: 'end',
        })}
      >
        Save Changes
      </Button>
    </StyledCard>
  );
};

export const IncidentDetailsWrapper = () => {
  const { id } = useParams<{ id: string }>();
  const incident = useIncidentById(id);

  if (incident.isInitialLoading && !incident.data)
    return <LoadingOverlay visible />;
  else if (!incident.data) return <Navigate to="/incidents" replace />;

  return <IncidentDetails incident={incident.data} />;
};

const StyledCard = styled(Card)`
  .info-table-row {
    grid-template-columns: 200px 1fr;
  }
`;
